import React, { useCallback, useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { runJob } from "@/lib/jobs";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LINES = [["hc_active", "HEAD COUNT - ACTIVE"], ["hc_hold", "HEAD COUNT - HOLD"], ["basic", "(+) BASIC PAY"], ["overtime", "(+) OVERTIME"],
  ["allowances", "(+) ALLOWANCES"], ["wtax", "(-) WITHHOLDING TAX"], ["gov", "(-) GOV'T CONTRIBUTIONS (EE)"], ["loans", "(-) LOANS PAYABLE"],
  ["other", "(-) OTHER DEDUCTIONS"], ["net", "NET PAY"]];
const STATUS = ["", "Pending", "Approved", "Sent Back"];
const money = (v) => (v == null ? "-" : Math.abs(v) < 0.005 ? "-" : v < 0 ? `(${Math.abs(v).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : v.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmt = (k, v) => (k.startsWith("hc") ? String(Math.round(v || 0)) : money(v));
const longDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
const shortDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase();
const badge = (s) => (s === "Approved" ? "bg-emerald-100 text-emerald-800" : s === "Sent Back" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800");

function MemoList({ items, sel, onSel, status, setStatus }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 lg:w-80 shrink-0" data-testid="memo-list">
      <div className="flex flex-wrap gap-1 mb-3">{STATUS.map((s) => (
        <button key={s || "all"} onClick={() => setStatus(s)} className={`px-2.5 py-1 rounded-md text-xs font-medium ${status === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:text-slate-900"}`} data-testid={`memo-filter-${s || "all"}`}>{s || "All"}</button>))}</div>
      {!items.length && <div className="text-sm text-slate-400 py-8 text-center" data-testid="memo-empty">No payroll memos yet. Authorize a batch in Payroll Master.</div>}
      <div className="space-y-1.5 max-h-[70vh] overflow-y-auto">{items.map((m) => (
        <button key={m.id} onClick={() => onSel(m.id)} className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${sel === m.id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`memo-item-${m.id}`}>
          <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold text-slate-900 truncate">{m.company_name}</span>
            <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${badge(m.status)}`}>{m.status}</span></div>
          <div className="text-xs text-slate-500 mt-0.5">Payout {longDate(m.payout_date)} · v{m.version}</div>
          <div className="text-xs text-slate-400">{m.cutoff_start} → {m.cutoff_end}</div>
          {m.my_turn && <div className="text-[11px] font-semibold text-blue-700 mt-1" data-testid={`memo-myturn-${m.id}`}>Waiting for your approval</div>}
        </button>))}</div>
    </div>
  );
}

function SummaryTable({ s }) {
  const last = `${shortDate(s.prev_payout_date)} PAYOUT`;
  const pctCls = (p) => (p == null || p === 0 ? "" : p > 0 ? "text-rose-600" : "text-emerald-600");
  return (
    <div className="overflow-x-auto"><table className="w-full text-xs border-collapse min-w-[760px]" data-testid="memo-summary-table">
      <thead><tr className="border-b-2 border-slate-800">{["ACCOUNT", "MONTHLY", "DAILY", "CONSULTANT", "TOTAL", last, "DIFFERENCE Inc(+)/Dec(-)", "PERCENTAGE"].map((h, i) => (
        <th key={h} className={`py-2 px-2 font-semibold ${i ? "text-right" : "text-left"} ${i === 5 ? "text-blue-700" : ""}`}>{h}</th>))}</tr></thead>
      <tbody>{LINES.map(([k, l]) => (
        <tr key={k} className={`border-b border-slate-100 ${k === "net" ? "font-bold border-t-2 border-t-slate-800" : ""}`} data-testid={`memo-line-${k}`}>
          <td className={`py-1.5 px-2 ${k.startsWith("hc") ? "italic font-semibold" : ""}`}>{l}</td>
          {["monthly", "daily", "consultant", "total"].map((c) => <td key={c} className="py-1.5 px-2 text-right tabular-nums">{fmt(k, s[c][k])}</td>)}
          <td className="py-1.5 px-2 text-right tabular-nums text-blue-700">{s.has_last ? fmt(k, s.last[k]) : "-"}</td>
          <td className={`py-1.5 px-2 text-right tabular-nums ${pctCls(s.pct[k])}`}>{s.has_last ? fmt(k, s.diff[k]) : "-"}</td>
          <td className={`py-1.5 px-2 text-right tabular-nums ${pctCls(s.pct[k])}`}>{s.pct[k] == null ? "-" : `${s.pct[k].toFixed(0)}%`}</td>
        </tr>))}</tbody>
    </table></div>
  );
}

function BankSummary({ s }) {
  const bt = s.bank_total;
  return (
    <div className="grid lg:grid-cols-[1fr_280px] gap-4 mt-5">
      <div className="overflow-x-auto"><table className="w-full text-xs border-collapse min-w-[480px]" data-testid="memo-bank-table">
        <thead><tr className="border-b border-slate-300">{["BANK SUMMARY", "MONTHLY", "DAILY", "CONSULTANT", "TOTAL"].map((h, i) => <th key={h} className={`py-1.5 px-2 font-semibold ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
        <tbody>{s.banks.map((b) => (
          <tr key={b.bank} className="border-b border-slate-100"><td className="py-1 px-2 font-medium">{b.bank}</td>
            {["monthly", "daily", "consultant", "total"].map((c) => <td key={c} className="py-1 px-2 text-right tabular-nums">{money(b[c])}</td>)}</tr>))}
          <tr className="font-bold border-t-2 border-slate-800" data-testid="memo-bank-total"><td className="py-1.5 px-2">TOTAL BANK SUMMARY</td>
            {["monthly", "daily", "consultant", "total"].map((c) => <td key={c} className="py-1.5 px-2 text-right tabular-nums">{money(bt[c])}</td>)}</tr>
          <tr><td className="py-1 px-2 font-semibold">VS {shortDate(s.prev_payout_date)}</td><td colSpan={3} /><td className="py-1 px-2 text-right tabular-nums text-blue-700">{s.has_last ? money(s.vs_last) : "-"}</td></tr>
          <tr className="text-rose-600 font-semibold" data-testid="memo-vs-diff"><td className="py-1 px-2">DIFFERENCE</td><td colSpan={3} /><td className="py-1 px-2 text-right tabular-nums">{s.has_last ? money(s.vs_diff) : "-"}</td></tr>
        </tbody></table></div>
      <div className="rounded-lg border border-slate-200 p-3 text-xs space-y-1.5 h-fit" data-testid="memo-new-hires">
        <div className="font-semibold text-slate-700 mb-1">Newly hired this month</div>
        <div className="flex justify-between"><span>Newly hired</span><b>{s.new_hires}</b></div>
        <div className="flex justify-between"><span>Monthly salary</span><b className="tabular-nums">{money(s.new_hire_salary)}</b></div>
        <div className="flex justify-between border-t pt-1.5"><span>Total cost for newly hired</span><b className="tabular-nums">{money(s.new_hire_salary)}</b></div>
      </div>
    </div>
  );
}

function Signatories({ steps, current, status }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-6" data-testid="memo-signatories">
      {steps.map((s, i) => {
        const here = status === "Pending" && i === current;
        return (
          <div key={s.level} className={`rounded-lg border p-3 ${here ? "border-blue-400 bg-blue-50/60" : "border-slate-200"}`} data-testid={`memo-step-${s.level}`}>
            <div className="text-[11px] text-slate-500">{s.title}:</div>
            <div className="h-14 flex items-end">{s.status === "Approved" && s.signature ? <img src={s.signature} alt="e-signature" className="max-h-14 max-w-full object-contain" /> : <span className="text-[11px] text-slate-500 italic">{here ? "Awaiting signature" : s.status === "Approved" ? "Signed (no image)" : ""}</span>}</div>
            <div className="text-xs font-bold text-slate-900 uppercase truncate">{s.name || "Not designated"}</div>
            <div className="text-[11px] text-blue-700">{s.rank}</div>
            <div className="text-[10px] text-slate-400 mt-1">{s.status}{s.at ? ` · ${new Date(s.at).toLocaleString()}` : ""}</div>
          </div>
        );
      })}
    </div>
  );
}

function History({ items }) {
  return (
    <div className="mt-6" data-testid="memo-history"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">History</div>
      <ol className="space-y-1.5">{items.map((h, i) => (
        <li key={`${h.at}-${h.action}-${i}`} className="text-xs text-slate-600 flex gap-2"><span className="text-slate-400 w-36 shrink-0">{new Date(h.at).toLocaleString()}</span><span><b>{h.action}</b> · {h.by}{h.note ? ` — ${h.note}` : ""}</span></li>))}</ol>
    </div>
  );
}

function ActionDialog({ mode, onClose, onDone, memo }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/hris/payroll-memo/memos/${memo.id}/${mode === "approve" ? "approve" : "send-back"}`, { note }); toast.success(mode === "approve" ? "Approved and e-signed" : "Sent back to Payroll Master"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  const step = memo.steps[memo.current];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`memo-${mode}-dialog`}>
        <DialogHeader><DialogTitle>{mode === "approve" ? `Approve · ${step.title}` : "Send back for review"}</DialogTitle>
          <DialogDescription>{mode === "approve" ? "Your uploaded e-signature will be stamped on the memo and the next approver is notified." : "The memo is returned and the Payroll Master batch is unlocked so it can be corrected, verified and authorized again."}</DialogDescription></DialogHeader>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={mode === "approve" ? "Note (optional)" : "Reason (required)"} data-testid="memo-action-note" />
        <DialogFooter><Button variant="outline" onClick={onClose} data-testid="memo-action-cancel">Cancel</Button>
          <Button className={mode === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-600 hover:bg-amber-700"} disabled={busy || (mode !== "approve" && !note.trim())} onClick={go} data-testid="memo-action-confirm">{busy ? "Saving…" : mode === "approve" ? "Approve & Sign" : "Send Back"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MemoDoc({ memo, reload }) {
  const [mode, setMode] = useState(null);
  const pdf = () => runJob(() => api.get(`/hris/payroll-memo/memos/${memo.id}/pdf?background=true`).then((r) => r.data)).catch(() => {});
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 flex-1 min-w-0" data-testid="memo-doc">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="text-sm space-y-0.5">
          <div><span className="italic font-bold text-[#1F3864] w-24 sm:w-28 inline-block align-top">SUBJECT:</span><b>{memo.subject}</b></div>
          <div><span className="italic font-bold text-[#1F3864] w-24 sm:w-28 inline-block align-top">COMPANY:</span><b className="uppercase" data-testid="memo-company">{memo.company_name}</b></div>
          <div><span className="italic font-bold text-[#1F3864] w-24 sm:w-28 inline-block align-top">PAY OUT DATE:</span><b data-testid="memo-payout">{longDate(memo.payout_date)}</b></div>
          <div className="text-xs text-slate-500">{memo.reference} · cut-off {memo.cutoff_start} → {memo.cutoff_end}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <span className={`text-xs rounded-full px-2.5 py-1 font-semibold ${badge(memo.status)}`} data-testid="memo-status">{memo.status}</span>
          <Button size="sm" variant="outline" onClick={pdf} data-testid="memo-pdf-btn"><Icons.FileDown className="h-3.5 w-3.5 mr-1" />PDF</Button>
          {memo.can_send_back && <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" onClick={() => setMode("back")} data-testid="memo-send-back-btn"><Icons.Undo2 className="h-3.5 w-3.5 mr-1" />Send Back</Button>}
          {memo.my_turn && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setMode("approve")} data-testid="memo-approve-btn"><Icons.PenLine className="h-3.5 w-3.5 mr-1" />Approve</Button>}
        </div>
      </div>
      {memo.status === "Sent Back" && <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 mb-4" data-testid="memo-sent-back-note">Sent back by {memo.sent_back_by}: {memo.sent_back_reason}</div>}
      <SummaryTable s={memo.summary} />
      <BankSummary s={memo.summary} />
      <Signatories steps={memo.steps} current={memo.current} status={memo.status} />
      <History items={memo.history || []} />
      {mode && <ActionDialog mode={mode} memo={memo} onClose={() => setMode(null)} onDone={() => { setMode(null); reload(); }} />}
    </div>
  );
}

export default function PayrollMemo() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState("");
  const [memo, setMemo] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/hris/payroll-memo/memos?status=${encodeURIComponent(status)}`);
      setItems(data); setSel((s) => (data.some((m) => m.id === s) ? s : data[0]?.id || ""));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load memos"); }
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const loadMemo = useCallback(async () => {
    if (!sel) { setMemo(null); return; }
    try { setMemo((await api.get(`/hris/payroll-memo/memos/${sel}`)).data); } catch (e) { toast.error(e.response?.data?.detail || "Could not load memo"); }
  }, [sel]);
  useEffect(() => { loadMemo(); }, [loadMemo]);
  return (
    <div className="uemp-fade-in" data-testid="payroll-memo-page">
      <PageHeader title="Payroll Memo" subtitle="Payroll Summary Consolidated per company and cut-off. Prepared → Reviewed → Noted → Recommending Approval → Approved, each with e-signature. Fully approved memos move to Bank Uploading." />
      <div className="flex flex-col lg:flex-row gap-4">
        <MemoList items={items} sel={sel} onSel={setSel} status={status} setStatus={setStatus} />
        {memo ? <MemoDoc memo={memo} reload={() => { load(); loadMemo(); }} /> : <div className="flex-1 bg-white rounded-xl border border-dashed border-slate-300 grid place-items-center text-sm text-slate-400 min-h-[300px]">Select a memo</div>}
      </div>
    </div>
  );
}
